import { constants, utils, helpers } from '@cloudbase/utilities'
import { getStringByteLength } from './encoding'
import {
  ICloudbaseFileMetaDataRes,
  ICloudbaseFileInfo,
  ICloudbaseUploadFileParams,
  ICloudbaseUploadFileResult,
  ICloudbaseGetUploadMetadataParams,
  ICloudbaseDeleteFileParams,
  ICloudbaseDeleteFileResult,
  ICloudbaseGetTempFileURLResult,
  ICloudbaseGetTempFileURLParams,
  ICloudbaseDownloadFileResult,
  ICloudbaseDownloadFileParams,
  ICloudbaseCopyFileParams,
  ICloudbaseCopyFileResult,
} from '@cloudbase/types/storage'

export enum EUploadMethod {
  put = 'put',
  post = 'post',
}

export interface IUploadMetadata {
  url: string
  token: string
  authorization: string
  fileId: string
  cosFileId: string
  download_url?: string
}

const { getSdkName, ERRORS, COMMUNITY_SITE_URL } = constants
const { isArray, isString, isPalinObject, execCallback } = utils
/** catchErrorsDecorator 装饰器要放在index入口文件，意面有的编译找不到其sourcemap，例如HBuilderX */
const { catchErrorsDecorator } = helpers as { catchErrorsDecorator: (options: unknown) => MethodDecorator }

export const COMPONENT_NAME = 'storage'

/**
 * 网关（GATEWAY 模式）下的对象操作集合，统一走 `/v1/storages/*` HTTP 接口。
 *
 * `objectId` 第一段被解释为 `bucket_id`、其余为对象名（§1.2.2），
 * 权限由 `storage.objects` / `storage.buckets` 的 RLS 策略判定。
 *
 * 注意：这组接口与原生对象 API（StorageFileApi，`/object/{bucketId}/{name}`）
 * 是两套不同通道——前者是 `/v1/storages/*` 兼容路径，后者是原生 `bucketId + objectName` 路径。
 */
type CustomReqOpts = ICloudbaseUploadFileParams['customReqOpts']

type GatewayObjectResult = {
  code?: string
  message?: string
  authorization?: string
  token?: string
  uploadUrl?: string
  cloudObjectId?: string
  cloudObjectMeta?: string
  downloadUrl?: string
}

type GatewayResponse<T> = Record<string, unknown> & {
  data?: T
  requestId?: string
  code?: string
}

type GatewayRequest = {
  gateWay(payload: unknown, customReqOpts?: CustomReqOpts): Promise<GatewayResponse<GatewayObjectResult[]>>
}

/**
 * 网关获取 COS 上传预签名时使用的签名头，不等同于实际 XHR 上传请求头。
 *
 * 需要重点关注的 key：
 * - `content-length`：PUT body 的字节长度。后端用它参与 COS 签名，也会用于校验 Bucket 的 `file_size_limit` 配置。
 * - `content-type`：上传对象的 MIME 类型。后端用它参与 COS 签名，也会用于校验 Bucket 的 `allowed_mime_types` 配置；实际 PUT 上传时也需要发送同值。
 *
 * 调用方显式传入的其他 headers 会按原样放入签名头；这些 header 一旦参与签名，实际上传请求也必须发送同名同值 header。
 */
type SignedHeader = Record<string, string[]>

const storageGateWay = {
  getUploadInfo: async (
    request: GatewayRequest,
    params: { path: string; signedHeader?: SignedHeader },
    customReqOpts: CustomReqOpts,
  ) => {
    const res = await request.gateWay(
      {
        path: 'storages',
        name: 'get-objects-upload-info',
        data: [
          {
            objectId: params.path,
            ...(params.signedHeader ? { signedHeader: params.signedHeader } : {}),
          },
        ],
      },
      customReqOpts,
    )
    const data = res.data?.[0] || {}

    return {
      ...res,
      data: {
        ...(data.code ? { ...data } : {}),
        authorization: data.authorization,
        token: data.token,
        url: data.uploadUrl,
        fileId: data.cloudObjectId,
        cosFileId: data.cloudObjectMeta,
        download_url: data.downloadUrl,
      },
    } as unknown as ICloudbaseFileMetaDataRes
  },
  getDownLoadInfo: async (
    request: GatewayRequest,
    params: { convertedFileList: Array<{ fileid: string; max_age?: number }> },
    customReqOpts: CustomReqOpts,
  ) => {
    const res = await request.gateWay(
      {
        path: 'storages',
        name: 'get-objects-download-info',
        data: params.convertedFileList.map(v => ({ cloudObjectId: v.fileid })),
      },
      customReqOpts,
    )
    return {
      ...res,
      data: {
        download_list: res.data?.map(v => ({
          code: v.code || 'SUCCESS',
          message: v.message,
          fileid: v.cloudObjectId,
          download_url: v.downloadUrl,
          fileID: v.cloudObjectId,
          tempFileURL: v.downloadUrl,
        })),
      },
    }
  },
  delete: async (
    request: GatewayRequest,
    params: { fileList: Array<string> },
    customReqOpts: CustomReqOpts,
  ) => {
    const res = await request.gateWay(
      {
        path: 'storages',
        name: 'delete-objects',
        data: params.fileList.map(v => ({ cloudObjectId: v })),
      },
      customReqOpts,
    )
    return {
      ...res,
      data: {
        delete_list: res.data?.map(v => ({
          code: v.code || 'SUCCESS',
          fileID: v.cloudObjectId,
          message: v.message,
        })),
      },
    }
  },
  copyFile: async (
    request: GatewayRequest,
    params: {
      convertedFileList: Array<{ src_path: string; dst_path: string; overwrite?: boolean; remove_original?: boolean }>
    },
    customReqOpts: CustomReqOpts,
  ) => {
    const res = await request.gateWay(
      {
        path: 'storages',
        name: 'copy-objects',
        data: params.convertedFileList.map(v => ({
          srcPath: v.src_path,
          dstPath: v.dst_path,
          overwrite: v.overwrite,
          removeOriginal: v.remove_original,
        })),
      },
      customReqOpts,
    )
    return {
      ...res,
      data: {
        copy_list: res.data?.map(v => ({
          code: v.code || 'SUCCESS',
          fileID: v.cloudObjectId,
          message: v.message,
        })),
      },
    }
  },
}

export interface ICloudbaseContext {
  config: any
  request: any
}

type TempFileURLItem = NonNullable<ICloudbaseGetTempFileURLResult['fileList']>[number]
type FileInfoItem = Pick<TempFileURLItem, 'code' | 'fileID' | 'tempFileURL'> & {
  message?: TempFileURLItem['message']
  fileName?: string
  cloudId?: TempFileURLItem['fileID']
  contentType?: string
  mime?: string
  size?: number
  cacheControl?: string
  lastModified?: string
  etag?: string
}

/** 常见扩展名 → MIME 类型，用于上传时推断 content-type。 */
const MIME_BY_EXT: Record<string, string> = {
  txt: 'text/plain',
  json: 'application/json',
  html: 'text/html',
  htm: 'text/html',
  css: 'text/css',
  js: 'application/javascript',
  csv: 'text/csv',
  xml: 'application/xml',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  ico: 'image/x-icon',
  pdf: 'application/pdf',
  zip: 'application/zip',
  gz: 'application/gzip',
  mp4: 'video/mp4',
  webm: 'video/webm',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
}

/**
 * 推断上传内容的 content-type，按可靠性从高到低：
 *  ① 内容自带的 MIME（File/Blob.type，由浏览器/系统给出，最可靠）；
 *  ② 按云端路径扩展名映射；
 *  ③ 兜底 application/octet-stream。
 * @param file 上传内容（可能是 Blob/File，也可能是字符串/ArrayBuffer 等无 type 形态）
 * @param path 云端路径，用于按扩展名兜底推断
 */
function inferContentType(file: any, path: string): string {
  if (typeof Blob !== 'undefined' && file instanceof Blob && file.type) {
    return file.type
  }
  const m = /\.([a-zA-Z0-9]+)$/.exec(path || '')
  const ext = m ? m[1].toLowerCase() : ''
  return MIME_BY_EXT[ext] || 'application/octet-stream'
}

/**
 * 尽力计算上传内容的字节长度（content-length）。
 *
 * 支持（可准确得到字节数）：
 *  - Blob / File：取 .size；
 *  - ArrayBuffer：取 .byteLength；
 *  - TypedArray / DataView（含 Node Buffer，为 Uint8Array 子类）：取 .byteLength；
 *  - 字符串「内容」：按 UTF-8 计算（浏览器用 new Blob([s]).size，Node 回退 Buffer.byteLength）；
 *  - 带数字 size 字段的对象：取 .size。
 *
 * 不支持（返回 undefined）：
 *  - 可读流：Node fs.ReadStream / Web ReadableStream —— 长度未知、边读边传，与「预签名 PUT 需先知长度」的模型冲突，无法同步求值；
 *  - FormData：用于 POST multipart 上传，不会进入 PUT 网关分支，故不处理；
 *  - 小程序本地路径字符串（wx 的 filePath 是文件路径而非内容）：
 *    注意会被误当作「字符串内容」计算成路径本身的字节数，调用方如走该形态应显式传 content-length；
 *  - 其它无 size 信息的自定义对象。
 */
function resolveUploadByteLength(file: any, fileContent: any): number | undefined {
  const target = file ?? fileContent
  if (target === null) return undefined
  if (typeof Blob !== 'undefined' && target instanceof Blob) return target.size
  if (typeof ArrayBuffer !== 'undefined') {
    if (target instanceof ArrayBuffer) return target.byteLength
    if (typeof ArrayBuffer.isView === 'function' && ArrayBuffer.isView(target)) {
      return (target as ArrayBufferView).byteLength
    }
  }
  if (typeof target === 'string') {
    if (typeof Blob !== 'undefined') return new Blob([target]).size
    const byteLength = getStringByteLength(target)
    if (typeof byteLength === 'number') return byteLength
    return target.length
  }
  if (typeof target === 'object' && typeof (target as any).size === 'number') {
    return (target as any).size
  }
  return undefined
}

/** 判断 headers（不区分大小写）是否已包含某个字段。 */
function hasHeader(headers: Record<string, string>, name: string): boolean {
  const lower = name.toLowerCase()
  return Object.keys(headers).some(k => k.toLowerCase() === lower)
}

function toSignedHeader(headers: Record<string, string>): SignedHeader {
  return Object.keys(headers).reduce(
    (result: SignedHeader, key) => ({ ...result, [key]: [headers[key]] }),
    {},
  )
}

/**
 * 传统云存储基类：提供顶层对象操作（`uploadFile` / `getTempFileURL` / `downloadFile` /
 * `deleteFile` / `copyFile` / `getFileInfo`），即「传统 SDK API」。
 *
 * 这些方法由 `isGateWay()` 决定底层通道（GATEWAY 走 `/v1/storages/*`，否则走旧版 CLOUD_API `request.send`）。
 * `cloudPath` 第一段须为 `bucket_id`、其余为对象名（§1.2.2）；`fileID` 形态为
 * `cloud://env.cosBucket/<bucketId>/<name>`（路径含 bucket 段），权限由 PostgreSQL RLS（基于 `bucket_id`）判定。
 * 上传需 content-length / content-type 参与 COS 签名及 Bucket 配置校验（`uploadFile` 已自动补全）。
 *
 * @remarks 若 `cloudPath` 不含 bucket 前缀（如 `a.txt` 或首段桶不存在），服务端报
 * `STORAGE_BUCKET_NOT_FOUND`；缺 content-length 则报 `STORAGE_CONTENT_LENGTH_REQUIRED`。
 */
export class CloudbaseStorage {
  /**
   * 运行上下文（`{ config, request }`）。
   *
   * - 组件模式：实例由 `new CloudbaseStorage()`（无参）创建，方法被提取后重新绑定到 cloudbase 实例，
   *   `this.config` / `this.request` 由该实例提供，本字段为空、原型 getter 不参与解析。
   * - 组合模式：由 `StorageClient`（入口 `supabaseComponent`）注入 context，方法以本实例为 `this`
   *   运行，`this.config` / `this.request` 经下方 getter 从 context 读取。
   */
  protected context?: ICloudbaseContext

  constructor(context?: ICloudbaseContext) {
    this.context = context
  }

  // @ts-ignore — 组件模式下 this 为 cloudbase 实例、走其自身 config，本 getter 不参与解析
  get config() {
    // @ts-ignore
    return this.context?.config
  }

  // @ts-ignore — 同上
  get request() {
    return this.context?.request
  }

  /**
   * 是否为网关模式（GATEWAY）。
   *
   * 用于区分「走新版 `/v1/storages/*` 网关」还是「走旧版 CLOUD_API（`request.send`）」。
   */
  public isGateWay() {
    // @ts-ignore
    const { config } = this
    const endPointMode = config.endPointMode || 'CLOUD_API'

    return endPointMode === 'GATEWAY'
  }

  @catchErrorsDecorator({
    customInfo: {
      className: 'Cloudbase',
      methodName: 'uploadFile',
    },
    title: '上传文件失败',
    messages: [
      '请确认以下各项：',
      '  1 - 调用 uploadFile() 的语法或参数是否正确',
      '  2 - 当前域名是否在安全域名列表中：https://console.cloud.tencent.com/tcb/env/safety',
      '  3 - 云存储安全规则是否限制了当前登录状态访问',
      `如果问题依然存在，建议到官方问答社区提问或寻找帮助：${COMMUNITY_SITE_URL}`,
    ],
  })
  public async uploadFile(
    params: Omit<ICloudbaseUploadFileParams, 'filePath'> & { filePath?: string },
    callback?: Function,
  ): Promise<ICloudbaseUploadFileResult> {
    const { cloudPath, filePath, onUploadProgress, method = 'put', headers = {}, fileContent } = params
    if (!isString(cloudPath) || (!filePath && !fileContent)) {
      throw new Error(JSON.stringify({
        code: ERRORS.INVALID_PARAMS,
        msg: `[${COMPONENT_NAME}.uploadFile] invalid params`,
      }),)
    }
    const uploadMethod =      { put: EUploadMethod.put, post: EUploadMethod.post }[method.toLocaleLowerCase()] || EUploadMethod.put

    const isGateway = this.isGateWay()
    const signedHeaders: Record<string, string> = { ...headers }
    const uploadHeaders: Record<string, string> = Object.keys(headers).reduce((result: Record<string, string>, key) => {
      if (key.toLowerCase() !== 'content-length') {
        result[key] = headers[key]
      }
      return result
    }, {})

    // 网关 PUT 直传时，COS 签名需要 content-length / content-type 参与；
    // 也会用它们校验 Bucket 的 file_size_limit / allowed_mime_types 配置，
    // 否则后端 get-objects-upload-info 会报 STORAGE_CONTENT_LENGTH_REQUIRED 等错误。
    // content-length 仅用于 signedHeader，不污染实际 XHR headers；content-type 需要参与签名也需要实际发送。
    if (uploadMethod === EUploadMethod.put && isGateway) {
      if (!hasHeader(signedHeaders, 'content-length')) {
        const byteLength = resolveUploadByteLength(filePath, fileContent)
        if (typeof byteLength === 'number') {
          signedHeaders['content-length'] = String(byteLength)
        }
      }
      if (!hasHeader(signedHeaders, 'content-type')) {
        // 优先取内容自带的 MIME（Blob.type），否则按 cloudPath 扩展名兜底
        signedHeaders['content-type'] = inferContentType(filePath, cloudPath)
      }
      if (!hasHeader(uploadHeaders, 'content-type')) {
        uploadHeaders['content-type'] = inferContentType(filePath, cloudPath)
      }
    }

    // 调用 getUploadMetadata 获取上传元数据
    const metadataResult = await this.getUploadMetadata({
      cloudPath,
      method: uploadMethod,
      headers: uploadMethod === EUploadMethod.put && !isGateway ? uploadHeaders : undefined,
      signedHeader: uploadMethod === EUploadMethod.put && isGateway ? toSignedHeader(signedHeaders) : undefined,
      customReqOpts: params.customReqOpts,
    })

    const { data: metadata, requestId } = metadataResult

    // 网关获取上传信息失败（如 STORAGE_CONTENT_LENGTH_REQUIRED）时，metadata 会带 code/message
    // 而无 url/authorization/token；必须在此中断，避免拿空 url 继续发起无效 PUT 上传。
    const metaErr: any = metadata
    if (metaErr?.code || !metaErr?.url) {
      const code = metaErr?.code || ERRORS.OPERATION_FAIL
      const message = metaErr?.message || 'get upload info failed'
      return execCallback(
        callback,
        new Error(`[${getSdkName()}][${code}][${COMPONENT_NAME}]:${message}`),
      )
    }

    const { url, authorization, token, fileId, cosFileId, download_url: downloadUrl } = metadata

    const commonParams = {
      url,
      file: filePath,
      name: cloudPath,
      onUploadProgress,
      fileContent,
      fileId,
      requestId,
    }

    const putParams = {
      ...commonParams,
      method: EUploadMethod.put,
      headers: {
        ...uploadHeaders,
        authorization,
        'x-cos-meta-fileid': cosFileId,
        'x-cos-security-token': token,
      },
    }

    const postParams = {
      ...commonParams,
      method: EUploadMethod.post,
      data: {
        key: cloudPath,
        signature: authorization,
        'x-cos-meta-fileid': cosFileId,
        success_action_status: '201',
        'x-cos-security-token': token,
      },
    }

    const uploadConfig = {
      [EUploadMethod.put]: {
        params: putParams,
        isSuccess: (code: number) => code >= 200 && code < 300,
      },
      [EUploadMethod.post]: {
        params: postParams,
        isSuccess: (code: number) => code === 201,
      },
    }

    // @ts-ignore
    const res = await this.request.upload(uploadConfig[uploadMethod].params)

    if (uploadConfig[uploadMethod].isSuccess(res.statusCode)) {
      return execCallback(callback, null, {
        fileID: fileId,
        download_url: downloadUrl,
        requestId,
      })
    }
    return execCallback(
      callback,
      new Error(`[${getSdkName()}][${ERRORS.OPERATION_FAIL}][${COMPONENT_NAME}]:${res.data}`),
    )
  }

  @catchErrorsDecorator({
    customInfo: {
      className: 'Cloudbase',
      methodName: 'getUploadMetadata',
    },
    title: '获取上传元信息失败',
    messages: [
      '请确认以下各项：',
      '  1 - 调用 getUploadMetadata() 的语法或参数是否正确',
      '  2 - 当前域名是否在安全域名列表中：https://console.cloud.tencent.com/tcb/env/safety',
      '  3 - 云存储安全规则是否限制了当前登录状态访问',
      `如果问题依然存在，建议到官方问答社区提问或寻找帮助：${COMMUNITY_SITE_URL}`,
    ],
  })
  public async getUploadMetadata(
    params: ICloudbaseGetUploadMetadataParams & {
      method?: EUploadMethod
      headers?: Record<string, string>
      signedHeader?: SignedHeader
    },
    callback?: Function,
  ): Promise<{ data: IUploadMetadata; requestId: string }> {
    const { cloudPath, method, headers, signedHeader } = params
    if (!isString(cloudPath)) {
      throw new Error(JSON.stringify({
        code: ERRORS.INVALID_PARAMS,
        msg: `[${COMPONENT_NAME}.getUploadMetadata] invalid cloudPath`,
      }),)
    }
    // @ts-ignore
    const { request } = this
    const action = 'storage.getUploadMetadata'

    try {
      let metaData: ICloudbaseFileMetaDataRes

      const metaDataParam: {
        path: string
        method?: EUploadMethod
        headers?: Record<string, string>
        signedHeader?: SignedHeader
      } = { path: cloudPath }
      const isGateway = this.isGateWay()

      if (method) {
        metaDataParam.method = method
      }
      if (method === EUploadMethod.put) {
        if (isGateway && signedHeader) {
          metaDataParam.signedHeader = signedHeader
        } else if (!isGateway && headers) {
          metaDataParam.headers = headers
        }
      }

      if (isGateway) {
        metaData = await storageGateWay.getUploadInfo(request, metaDataParam, params.customReqOpts)
      } else {
        metaData = await request.send(action, metaDataParam, params.customReqOpts)
      }
      return execCallback(callback, null, metaData)
    } catch (err) {
      return execCallback(callback, err)
    }
  }

  /**
   * 删除文件（按 fileID 批量）。
   *
   * 服务端从 `cloudObjectId` 反解 `bucket_id + name`，先按 RLS 删 `storage.objects`
   * 元数据再删 COS 对象；调用方传的 fileID 应来自上传返回值（已含 bucket 段）。
   */
  @catchErrorsDecorator({
    customInfo: {
      className: 'Cloudbase',
      methodName: 'deleteFile',
    },
    title: '删除文件失败',
    messages: [
      '请确认以下各项：',
      '  1 - 调用 deleteFile() 的语法或参数是否正确',
      '  2 - 当前域名是否在安全域名列表中：https://console.cloud.tencent.com/tcb/env/safety',
      '  3 - 云存储安全规则是否限制了当前登录状态访问',
      `如果问题依然存在，建议到官方问答社区提问或寻找帮助：${COMMUNITY_SITE_URL}`,
    ],
  })
  public async deleteFile(
    params: ICloudbaseDeleteFileParams,
    callback?: Function,
  ): Promise<ICloudbaseDeleteFileResult> {
    const { fileList } = params

    if (!fileList || !isArray(fileList) || fileList.length === 0) {
      throw new Error(JSON.stringify({
        code: ERRORS.INVALID_PARAMS,
        msg: `[${COMPONENT_NAME}.deleteFile] fileList must not be empty`,
      }),)
    }

    for (const fileId of fileList) {
      if (!fileId || !isString(fileId)) {
        throw new Error(JSON.stringify({
          code: ERRORS.INVALID_PARAMS,
          msg: `[${COMPONENT_NAME}.deleteFile] fileID must be string`,
        }),)
      }
    }

    const action = 'storage.batchDeleteFile'
    // @ts-ignore
    const { request } = this
    let res: any = {}

    if (this.isGateWay()) {
      res = await storageGateWay.delete(request, { fileList }, params.customReqOpts)
    } else {
      res = await request.send(
        action,
        {
          fileid_list: fileList,
        },
        params.customReqOpts,
      )
    }

    if (res.code) {
      return execCallback(callback, null, res)
    }
    const data = {
      fileList: res.data.delete_list,
      requestId: res.requestId,
    }
    return execCallback(callback, null, data)
  }

  /**
   * 获取文件临时下载链接（按 fileID 批量）。
   *
   * 服务端从 `cloudObjectId` 拆出 `bucket_id + name`，先查元数据并执行 RLS 校验，
   * 再经 COS `HEAD` 确认物理文件后生成下载 URL（无权限或不存在则该项返回错误码）。
   */
  @catchErrorsDecorator({
    customInfo: {
      className: 'Cloudbase',
      methodName: 'getTempFileURL',
    },
    title: '获取文件下载链接',
    messages: [
      '请确认以下各项：',
      '  1 - 调用 getTempFileURL() 的语法或参数是否正确',
      '  2 - 当前域名是否在安全域名列表中：https://console.cloud.tencent.com/tcb/env/safety',
      '  3 - 云存储安全规则是否限制了当前登录状态访问',
      `如果问题依然存在，建议到官方问答社区提问或寻找帮助：${COMMUNITY_SITE_URL}`,
    ],
  })
  public async getTempFileURL(
    params: ICloudbaseGetTempFileURLParams,
    callback?: Function,
  ): Promise<ICloudbaseGetTempFileURLResult> {
    const { fileList } = params

    if (!fileList || !isArray(fileList) || fileList.length === 0) {
      throw new Error(JSON.stringify({
        code: ERRORS.INVALID_PARAMS,
        msg: `[${COMPONENT_NAME}.getTempFileURL] fileList must not be empty`,
      }),)
    }

    const convertedFileList: Array<{ fileid: string; max_age?: number }> = []
    for (const file of fileList) {
      if (isPalinObject(file)) {
        if (!Object.prototype.hasOwnProperty.call(file, 'fileID')) {
          throw new Error(JSON.stringify({
            code: ERRORS.INVALID_PARAMS,
            msg: `[${COMPONENT_NAME}.getTempFileURL] file info must include fileID`,
          }),)
        }

        convertedFileList.push({
          fileid: (file as ICloudbaseFileInfo).fileID,
          max_age: (file as ICloudbaseFileInfo).maxAge || 7200,
        })
      } else if (isString(file)) {
        convertedFileList.push({
          fileid: file as string,
        })
      } else {
        throw new Error(JSON.stringify({
          code: ERRORS.INVALID_PARAMS,
          msg: `[${COMPONENT_NAME}.getTempFileURL] invalid fileList`,
        }),)
      }
    }

    const action = 'storage.batchGetDownloadUrl'
    // @ts-ignore
    const { request } = this
    let res: any = {}

    if (this.isGateWay()) {
      res = await storageGateWay.getDownLoadInfo(request, { convertedFileList }, params.customReqOpts)
    } else {
      res = await request.send(action, { file_list: convertedFileList }, params.customReqOpts)
    }

    if (res.code) {
      return execCallback(callback, null, res)
    }

    return execCallback(callback, null, {
      fileList: res.data.download_list,
      requestId: res.requestId,
    })
  }

  /**
   * 下载文件，返回内容（内部先取临时 URL 再拉取）。
   *
   * 同 `getTempFileURL`，先经元数据 + RLS 校验再下载；`fileID` 须含 bucket 段。
   */
  @catchErrorsDecorator({
    customInfo: {
      className: 'Cloudbase',
      methodName: 'downloadFile',
    },
    title: '下载文件失败',
    messages: [
      '请确认以下各项：',
      '  1 - 调用 downloadFile() 的语法或参数是否正确',
      '  2 - 当前域名是否在安全域名列表中：https://console.cloud.tencent.com/tcb/env/safety',
      '  3 - 云存储安全规则是否限制了当前登录状态访问',
      `如果问题依然存在，建议到官方问答社区提问或寻找帮助：${COMMUNITY_SITE_URL}`,
    ],
  })
  public async downloadFile(
    params: ICloudbaseDownloadFileParams,
    callback?: Function,
  ): Promise<ICloudbaseDownloadFileResult> {
    const { fileID } = params
    if (!isString(fileID)) {
      throw new Error(JSON.stringify({
        code: ERRORS.INVALID_PARAMS,
        msg: `[${COMPONENT_NAME}.getTempFileURL] fileID must be string`,
      }),)
    }

    const tmpUrlRes = await this.getTempFileURL.call(this, {
      fileList: [
        {
          fileID,
          maxAge: 600,
        },
      ],
      customReqOpts: params.customReqOpts,
    })

    const res = tmpUrlRes.fileList![0]

    if (res.code !== 'SUCCESS') {
      return execCallback(callback, res)
    }
    // @ts-ignore
    const { request } = this

    const tmpUrl = encodeURI(res.download_url!)

    const result = await request.download({ url: tmpUrl, tempFilePath: params.tempFilePath })
    return execCallback(callback, null, result)
  }

  /**
   * 批量复制文件（按源/目标 fileID）。
   *
   * 服务端从源/目标路径各拆出 `bucket_id + name`，经 RLS 校验读写权限后执行
   * 底层 COS 复制（支持跨 bucket，前提是两个 bucket 段都解析成功且有权限）。
   */
  @catchErrorsDecorator({
    customInfo: {
      className: 'Cloudbase',
      methodName: 'copyFile',
    },
    title: '批量复制文件',
    messages: [
      '请确认以下各项：',
      '  1 - 调用 copyFile() 的语法或参数是否正确',
      '  2 - 当前域名是否在安全域名列表中：https://console.cloud.tencent.com/tcb/env/safety',
      '  3 - 云存储安全规则是否限制了当前登录状态访问',
      `如果问题依然存在，建议到官方问答社区提问或寻找帮助：${COMMUNITY_SITE_URL}`,
    ],
  })
  public async copyFile(params: ICloudbaseCopyFileParams, callback?: Function): Promise<ICloudbaseCopyFileResult> {
    const { fileList } = params

    if (!fileList || !isArray(fileList) || fileList.length === 0) {
      throw new Error(JSON.stringify({
        code: ERRORS.INVALID_PARAMS,
        msg: `[${COMPONENT_NAME}.copyFile] fileList must not be empty`,
      }),)
    }

    const convertedFileList = []

    for (const file of fileList) {
      const { srcPath, dstPath } = file
      if (!srcPath || !dstPath || typeof srcPath !== 'string' || typeof dstPath !== 'string') {
        throw new Error(JSON.stringify({
          code: ERRORS.INVALID_PARAMS,
          msg: `[${COMPONENT_NAME}.copyFile] srcPath and dstPath may not be empty`,
        }),)
      }
      if (srcPath === dstPath) {
        throw new Error(JSON.stringify({
          code: ERRORS.INVALID_PARAMS,
          msg: `[${COMPONENT_NAME}.copyFile] srcPath and dstPath can not be the same`,
        }),)
      }
      //   if (basename(srcPath) !== basename(dstPath)) {
      //     throw new Error(JSON.stringify({
      //       code: ERRORS.INVALID_PARAMS,
      //       msg: `[${COMPONENT_NAME}.copyFile] srcPath and dstPath file name must be the same`,
      //     }),)
      //   }
      convertedFileList.push({
        src_path: srcPath,
        dst_path: dstPath,
        overwrite: file.overwrite,
        remove_original: file.removeOriginal,
      })
    }

    // @ts-ignore
    const { request } = this
    let res: any = {}

    if (this.isGateWay()) {
      res = await storageGateWay.copyFile(request, { convertedFileList }, params.customReqOpts)
    } else {
      const action = 'storage.batchCopyFile'
      res = await request.send(action, { file_list: convertedFileList }, params.customReqOpts)
    }

    if (res.code) {
      return execCallback(callback, null, res)
    }

    return execCallback(callback, null, {
      fileList: res.data.copy_list,
      requestId: res.requestId,
    })
  }

  /**
   * 获取文件元信息（大小、contentType、etag、lastModified 等，内部基于临时 URL + COS HEAD）。
   *
   * 服务端解析 `bucket_id + name` 并通过 RLS 后返回；元数据以 COS 对象为准
   * （`storage.objects` 的完整元信息建议改用 `from(bucketId).info()`）。
   */
  public async getFileInfo(params: ICloudbaseGetTempFileURLParams): Promise<{
    fileList: FileInfoItem[]
    requestId: string
  }> {
    const fileInfo = await this.getTempFileURL(params)

    if (fileInfo?.fileList && fileInfo?.fileList?.length > 0) {
      const fileList = await Promise.all(fileInfo.fileList.map(async (item: TempFileURLItem) => {
        if (item.code !== 'SUCCESS') {
          return {
            code: item.code,
            fileID: item.fileID,
            tempFileURL: item.tempFileURL,
            message: item.message,
          }
        }

        try {
          // @ts-ignore
          const { request } = this
          const res = await request.fetch({ url: item.tempFileURL, method: 'HEAD' })
          let { header } = res

          if (typeof Headers !== 'undefined' && header instanceof Headers) {
            const normalizedHeader: Record<string, string> = {}
            header.forEach((value, key) => {
              normalizedHeader[key] = value
            })
            header = normalizedHeader
          }
          // eslint-disable-next-line radix
          const fileSize = parseInt(header['content-length']) || 0
          const contentType = header['content-type'] || ''

          if ([400, 404].includes(Number(res.statusCode))) {
            return {
              code: 'FILE_NOT_FOUND',
              fileID: item.fileID,
              tempFileURL: item.tempFileURL,
              message: 'file not found',
            }
          }

          const fileInfo: FileInfoItem = {
            code: item.code,
            fileID: item.fileID,
            tempFileURL: item.tempFileURL,
            cloudId: item.fileID,
            fileName: item.fileID.split('/').pop(),
            contentType,
            mime: contentType.split(';')[0].trim(),
            size: fileSize,
            etag: header.etag || '',
            lastModified: header['last-modified'] || '',
            cacheControl: header['cache-control'] || '',
          }

          return fileInfo
        } catch (e) {
          const message = typeof e === 'object' && e !== null && 'message' in e
            ? String((e as { message?: unknown }).message)
            : undefined
          return {
            code: 'FETCH_FILE_INFO_ERROR',
            fileID: item.fileID,
            tempFileURL: item.tempFileURL,
            message,
          }
        }
      }),)

      return {
        fileList,
        requestId: fileInfo.requestId!,
      }
    }

    return {
      fileList: [],
      requestId: fileInfo.requestId!,
    }
  }
}
